import { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import { ChevronLeft, LayoutGrid, List as ListIcon, Plus, MoreVertical } from "lucide-react";

import { taskboard } from "../../services/taskboardService";
import { ROLES } from "../../config/roles";
import AddLinkModal from "../../components/agency/AddLinkModal";
import AddEntryModal from "../../components/agency/AddEntryModal";
import ConfirmationModal from "../../components/club/ConfirmationModal";
import CountryFlag from "../../components/transfer-windows/CountryFlag";
import BoardView from "../../components/agency/taskboard/BoardView";
import TimelineView from "../../components/agency/taskboard/TimelineView";
import EntryThreadDrawer from "../../components/agency/taskboard/EntryThreadDrawer";
import LinkCard from "../../components/agency/taskboard/LinkCard";
import { lastActivityMs, relativeActivity } from "../../utils/taskboardActivity";
import { entryBoardStatus } from "../../constants/taskboardStatus";

// ──────────────────────────────────────────────────────────────────────
// FolderDetail — the per-player deal tracker. Two views over one entry
// set: BOARD (status columns, drag-and-drop) and TIMELINE (newest-first,
// month groups). Status changes persist in one gesture (drag) or one
// click (quick-select pill). Entries are editable, carry an append-only
// update thread, and a real club logo chip. External links render as
// typed cards below the views.
// ──────────────────────────────────────────────────────────────────────

function getInitials(name) {
  if (!name) return "?";
  return name.split(" ").filter(Boolean).map((w) => w[0]).join("").toUpperCase().slice(0, 2);
}

const FolderDetail = () => {
  const { folderId } = useParams();
  const navigate = useNavigate();
  const { currentRole, user } = useSelector((state) => state.auth);
  const isCurrentUserAdmin = currentRole === ROLES.AgencyAdmin;
  const currentUserAccountId = user?.account_id || user?.id || null;

  const [folder, setFolder] = useState(null);
  const [entries, setEntries] = useState([]);
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [view, setView] = useState("board");

  const [showAddLink, setShowAddLink] = useState(false);
  const [editLink, setEditLink] = useState(null);
  const [showEntryModal, setShowEntryModal] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [entryDefaultStatus, setEntryDefaultStatus] = useState("open");
  const [threadEntry, setThreadEntry] = useState(null);
  const [folderMenuOpen, setFolderMenuOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const refetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    const [folderRes, entriesRes, linksRes] = await Promise.all([
      taskboard.getFolderDetail(folderId),
      taskboard.getEntries(folderId),
      taskboard.getLinks(folderId),
    ]);
    if (!folderRes.success) {
      setError(folderRes.status === 404 ? "Folder not found or you do not have access." : folderRes.error || "Could not load folder");
      setLoading(false);
      return;
    }
    setFolder(folderRes.data || null);
    setEntries(Array.isArray(entriesRes.data) ? entriesRes.data : []);
    setLinks(Array.isArray(linksRes.data) ? linksRes.data : []);
    setLoading(false);
  }, [folderId]);

  useEffect(() => {
    refetchAll();
  }, [refetchAll]);

  // Keep the thread drawer's entry fresh after a refetch.
  useEffect(() => {
    if (!threadEntry) return;
    const fresh = entries.find((e) => String(e.id) === String(threadEntry.id));
    if (fresh && fresh !== threadEntry) setThreadEntry(fresh);
  }, [entries, threadEntry]);

  const canModifyEntry = useCallback(
    (entry) =>
      isCurrentUserAdmin ||
      (entry.agent && currentUserAccountId != null && String(entry.agent.account_id) === String(currentUserAccountId)),
    [isCurrentUserAdmin, currentUserAccountId],
  );

  // Optimistic status change (drag or quick-select), then persist.
  const handleStatusChange = async (entry, newStatus) => {
    if (entryBoardStatus(entry) === newStatus) return;
    const prev = entries;
    // Optimistically move the card: board_status drives placement, and
    // we mirror it onto status so the pill label updates instantly too.
    setEntries((list) => list.map((e) => (e.id === entry.id ? { ...e, status: newStatus, board_status: newStatus } : e)));
    const res = await taskboard.patchEntry(folderId, entry.id, { status: newStatus });
    if (!res.success) {
      setEntries(prev); // roll back
      toast.error(res.error || "Could not update status");
    }
  };

  const openCreate = (status = "open") => {
    setEditEntry(null);
    setEntryDefaultStatus(typeof status === "string" ? status : "open");
    setShowEntryModal(true);
  };
  const openEdit = (entry) => {
    setEditEntry(entry);
    setShowEntryModal(true);
  };

  const handleDeleteFolder = () => setConfirmAction({ type: "folder" });
  const handleDeleteLink = (link) => setConfirmAction({ type: "link", id: link.id });
  const handleDeleteEntry = (entry) => setConfirmAction({ type: "entry", id: entry.id });

  const cancelConfirm = () => {
    if (confirmLoading) return;
    setConfirmAction(null);
  };

  const confirmDelete = async () => {
    if (!confirmAction) return;
    setConfirmLoading(true);
    try {
      if (confirmAction.type === "folder") {
        const res = await taskboard.deleteFolder(folderId);
        if (!res.success) return toast.error(res.error || "Failed to delete folder");
        toast.success("Folder deleted");
        setConfirmAction(null);
        navigate("/agency/taskboard");
        return;
      }
      if (confirmAction.type === "link") {
        const res = await taskboard.deleteLink(folderId, confirmAction.id);
        if (!res.success) return toast.error(res.error || "Failed to delete link");
        toast.success("Link removed");
        setConfirmAction(null);
        refetchAll();
        return;
      }
      if (confirmAction.type === "entry") {
        const res = await taskboard.deleteEntry(folderId, confirmAction.id);
        if (!res.success) return toast.error(res.error || "Failed to delete entry");
        toast.success("Entry removed");
        setConfirmAction(null);
        if (threadEntry && String(threadEntry.id) === String(confirmAction.id)) setThreadEntry(null);
        refetchAll();
      }
    } finally {
      setConfirmLoading(false);
    }
  };

  const heartbeatMs = useMemo(() => lastActivityMs(folder, entries, links), [folder, entries, links]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F4F6FA] flex items-center justify-center">
        <p className="text-[#6B7280]">Loading folder…</p>
      </div>
    );
  }
  if (error || !folder) {
    return (
      <div className="min-h-screen bg-[#F4F6FA] flex flex-col items-center justify-center px-4">
        <p className="text-[#B91C1C] mb-4">{error || "Folder not available."}</p>
        <button onClick={() => navigate("/agency/taskboard")} className="px-4 py-2 bg-[#2283FF] text-white rounded-lg">Back to Taskboard</button>
      </div>
    );
  }

  const player = folder.player || {};
  const manager = folder.current_agent || {};
  const club = player.current_club || null;

  return (
    <div className="min-h-screen bg-[#F4F6FA]">
      {/* Header */}
      <div className="px-4 md:px-8 pt-5 pb-3">
        <button onClick={() => navigate("/agency/taskboard")} className="text-[13px] text-[#667085] hover:text-[#101828] inline-flex items-center gap-1 mb-3">
          <ChevronLeft className="w-4 h-4" /> Taskboard
          <span className="text-[#D0D5DD]">/</span>
          <span className="text-[#101828] font-medium">{player.name || "Folder"}</span>
        </button>

        <div className="flex items-center gap-4 flex-wrap">
          {player.image ? (
            <img src={player.image} alt={player.name || ""} className="w-[52px] h-[52px] rounded-full object-cover flex-none" referrerPolicy="no-referrer" />
          ) : (
            <span className="w-[52px] h-[52px] rounded-full bg-[#EAF2FF] text-[#1866CC] flex items-center justify-center text-[17px] font-bold flex-none">
              {getInitials(player.name)}
            </span>
          )}
          <div className="flex flex-col gap-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-[20px] font-bold tracking-[-.01em] text-[#101828] truncate">{player.name || "Unknown player"}</span>
              {player.nationality && <CountryFlag country={player.nationality} width={18} height={12} />}
            </div>
            <div className="flex items-center gap-2.5 text-[13px] text-[#667085] flex-wrap">
              {(player.position || club?.name) && <span>{[player.position, club?.name].filter(Boolean).join(" · ")}</span>}
              {manager.name && (<><span className="text-[#D0D5DD]">·</span><span>{manager.name}</span></>)}
              <span className="text-[#D0D5DD]">·</span>
              <span className="inline-flex items-center gap-1.5">
                <span className="w-[7px] h-[7px] rounded-full bg-[#12B76A]" />
                <span className="font-semibold text-[#101828]">Last activity {relativeActivity(heartbeatMs)}</span>
              </span>
            </div>
          </div>

          <div className="ml-auto flex items-center gap-2.5">
            {/* Board / Timeline toggle */}
            <div className="flex bg-[#EDF0F4] rounded-[10px] p-[3px]">
              <button
                onClick={() => setView("board")}
                className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-[13px] font-semibold ${view === "board" ? "bg-white shadow-sm text-[#101828]" : "text-[#667085]"}`}
              >
                <LayoutGrid className="w-3.5 h-3.5" /> Board
              </button>
              <button
                onClick={() => setView("timeline")}
                className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-[13px] font-semibold ${view === "timeline" ? "bg-white shadow-sm text-[#101828]" : "text-[#667085]"}`}
              >
                <ListIcon className="w-3.5 h-3.5" /> Timeline
              </button>
            </div>
            <button onClick={() => openCreate("open")} className="flex items-center gap-1.5 bg-[#2283FF] text-white rounded-[9px] px-4 py-2.5 text-[13.5px] font-semibold shadow-sm hover:bg-[#1866CC]">
              <Plus className="w-3.5 h-3.5" /> Add entry
            </button>
            {isCurrentUserAdmin && (
              <div className="relative">
                <button onClick={() => setFolderMenuOpen((o) => !o)} className="w-9 h-9 rounded-[9px] border border-[#E4E9F0] bg-white flex items-center justify-center text-[#667085] hover:bg-gray-50" aria-label="Folder actions">
                  <MoreVertical className="w-4 h-4" />
                </button>
                {folderMenuOpen && (
                  <div className="absolute right-0 top-full mt-1 z-30 w-44 bg-white border border-[#E4E9F0] rounded-lg shadow-lg py-1">
                    <button onClick={() => { setFolderMenuOpen(false); handleDeleteFolder(); }} className="block w-full text-left px-3 py-2 text-[13px] text-[#B91C1C] hover:bg-red-50">
                      Delete folder
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Views */}
      <div className="px-4 md:px-8 pb-6">
        {view === "board" ? (
          <BoardView
            entries={entries}
            canModify={canModifyEntry}
            onStatusChange={handleStatusChange}
            onEdit={openEdit}
            onDelete={handleDeleteEntry}
            onOpenThread={setThreadEntry}
            onAddEntry={openCreate}
          />
        ) : (
          <TimelineView
            entries={entries}
            canModify={canModifyEntry}
            onStatusChange={handleStatusChange}
            onEdit={openEdit}
            onDelete={handleDeleteEntry}
            onOpenThread={setThreadEntry}
          />
        )}
      </div>

      {/* External links */}
      <div className="px-4 md:px-8 pb-16">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-[15px] font-semibold text-[#101828]">
            External links {links.length > 0 && <span className="text-[#98A2B3] font-normal">({links.length})</span>}
          </h2>
          <button onClick={() => { setEditLink(null); setShowAddLink(true); }} className="flex items-center gap-1.5 border border-[#E4E9F0] bg-white rounded-[9px] px-3 py-1.5 text-[13px] font-medium text-[#344054] hover:bg-gray-50">
            <Plus className="w-3.5 h-3.5" /> Add link
          </button>
        </div>
        {links.length === 0 ? (
          <p className="text-[13.5px] text-[#98A2B3] py-6 text-center border border-dashed border-[#E4E9F0] rounded-[10px]">
            No links yet. Attach a Drive doc, scout report, highlight video or any URL.
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5 max-w-4xl">
            {links.map((link) => (
              <LinkCard
                key={link.id}
                link={link}
                canModify={isCurrentUserAdmin || (link.created_by && String(link.created_by.account_id) === String(currentUserAccountId))}
                onEdit={(l) => { setEditLink(l); setShowAddLink(true); }}
                onRemove={handleDeleteLink}
              />
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      <AddEntryModal
        isOpen={showEntryModal}
        onClose={() => { setShowEntryModal(false); setEditEntry(null); }}
        folderId={folderId}
        entry={editEntry}
        defaultStatus={entryDefaultStatus}
        onSuccess={() => refetchAll()}
      />
      <AddLinkModal
        isOpen={showAddLink}
        onClose={() => { setShowAddLink(false); setEditLink(null); }}
        folderId={folderId}
        link={editLink}
        onSuccess={() => refetchAll()}
      />
      {threadEntry && (
        <EntryThreadDrawer
          entry={threadEntry}
          folderId={folderId}
          canModify={canModifyEntry(threadEntry)}
          onStatusChange={handleStatusChange}
          onChanged={() => refetchAll()}
          onClose={() => setThreadEntry(null)}
        />
      )}
      <ConfirmationModal
        isOpen={!!confirmAction}
        onClose={cancelConfirm}
        onConfirm={confirmDelete}
        loading={confirmLoading}
        variant="v2"
        confirmText={confirmAction?.type === "folder" ? "Delete folder" : "Remove"}
        confirmButtonClass="bg-[#E52F2F] hover:bg-[#c92020]"
        message={
          confirmAction?.type === "folder"
            ? "Delete this folder? Its entries and links go with it (soft delete, recoverable by an admin)."
            : confirmAction?.type === "link"
            ? "Remove this link?"
            : confirmAction?.type === "entry"
            ? "Remove this entry? It moves to a deleted state (no hard delete) along with its updates."
            : ""
        }
      />
    </div>
  );
};

export default FolderDetail;
